import React, { useEffect, useRef, useState } from 'react';
import { api, imageUrl } from '../services/api';
import { fileToCompressedDataUrl } from '../utils/compressImage';

const MAX = 30;

async function fileToDataUrl(file) {
  return fileToCompressedDataUrl(file);
}

/** Editor de sub-fotos / sabores. Sirve para cualquier producto, no solo exfoliantes. */
export function ProductOpcionesEditor({ product, onUploaded }) {
  const fileRef = useRef(null);
  const [opciones, setOpciones] = useState(() => product?.opciones || []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [warn, setWarn] = useState('');
  const [uploadFor, setUploadFor] = useState(null);

  useEffect(() => {
    setErr('');
    setWarn('');
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getProductoOpciones(product.id);
        if (!cancelled) {
          const ops = res.data?.opciones || [];
          if (ops.length) setOpciones(ops);
          else if (product.imagen) {
            setOpciones([
              {
                id: null,
                nombre: '',
                imagen: product.imagen,
                disponible: true,
                virtual: true,
              },
            ]);
          } else setOpciones([]);
        }
      } catch {
        if (!cancelled) {
          setOpciones(
            (product.opciones || []).length
              ? product.opciones
              : product.imagen
                ? [{ id: null, nombre: '', imagen: product.imagen, disponible: true, virtual: true }]
                : []
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [product?.id, product?.imagen, product?.opciones]);

  if (!product) return null;

  function applyPayload(res) {
    const data = res?.data || {};
    if (Array.isArray(data.opciones)) setOpciones(data.opciones);
    if (data.warning) setWarn(data.warning);
    onUploaded?.({
      ...product,
      imagen: data.imagen !== undefined ? data.imagen : product.imagen,
      opciones: data.opciones || opciones,
    });
  }

  async function ensureRealOpciones() {
    if (opciones.some((o) => o.id)) return opciones;
    const res = await api.addProductoOpcion(product.id, { materialize_only: true });
    const ops = res.data?.opciones || [];
    setOpciones(ops);
    return ops;
  }

  async function onPickFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    const target = uploadFor;
    setUploadFor(null);
    if (!file || !target) return;
    if (!/^image\/(png|jpe?g|webp|gif)$/i.test(file.type)) {
      setErr('Usa PNG, JPG, WEBP o GIF');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setErr('La imagen supera 10 MB');
      return;
    }
    setBusy(true);
    setErr('');
    setWarn('');
    try {
      const data_url = await fileToDataUrl(file);
      let oid = target.id;
      if (!oid) {
        const ops = await ensureRealOpciones();
        oid = ops[0]?.id;
      }
      if (!oid) {
        const added = await api.addProductoOpcion(product.id, { nombre: '' });
        oid = added.data?.opciones?.slice(-1)[0]?.id;
      }
      const res = await api.uploadProductoOpcionImagen(product.id, oid, {
        data_url,
        original_name: file.name,
      });
      applyPayload(res);
    } catch (ex) {
      const msg = String(ex.message || '');
      if (/413/.test(msg)) {
        setErr('La foto es muy pesada para el servidor. Prueba con JPG más pequeño o espera a que suban el fix del API.');
      } else {
        setErr(msg || 'No se pudo subir la foto');
      }
    } finally {
      setBusy(false);
    }
  }

  async function addSlot() {
    if (opciones.filter((o) => o.id || o.imagen).length >= MAX) return;
    setBusy(true);
    setErr('');
    try {
      const res = await api.addProductoOpcion(product.id, { nombre: '', disponible: true });
      applyPayload(res);
    } catch (ex) {
      setErr(ex.message || 'No se pudo agregar la opción');
    } finally {
      setBusy(false);
    }
  }

  async function toggleDisp(op) {
    setBusy(true);
    setErr('');
    try {
      let oid = op.id;
      if (!oid) {
        const ops = await ensureRealOpciones();
        oid = ops[0]?.id;
      }
      if (!oid) return;
      const res = await api.patchProductoOpcion(product.id, oid, { disponible: !op.disponible });
      applyPayload(res);
    } catch (ex) {
      setErr(ex.message || 'No se pudo cambiar la disponibilidad');
    } finally {
      setBusy(false);
    }
  }

  async function saveNombre(op, nombre) {
    if (!op.id) return;
    try {
      const res = await api.patchProductoOpcion(product.id, op.id, { nombre, disponible: op.disponible });
      applyPayload(res);
    } catch (ex) {
      setErr(ex.message || 'No se pudo guardar el nombre');
    }
  }

  async function removeOp(op) {
    if (!op.id) return;
    if (!window.confirm(`¿Quitar "${op.nombre || 'esta sub-opción'}"? El producto y las demás fotos se quedan.`)) {
      return;
    }
    setBusy(true);
    setErr('');
    try {
      const res = await api.deleteProductoOpcion(product.id, op.id);
      applyPayload(res);
    } catch (ex) {
      setErr(ex.message || 'No se pudo quitar');
    } finally {
      setBusy(false);
    }
  }

  async function clearAllPhotos() {
    const tieneFoto = Boolean(product.imagen) || slots.some((o) => o.imagen);
    if (!tieneFoto) return;
    if (
      !window.confirm(
        '¿Dejar este producto SIN foto?\n\nSe quita la portada y todas las sub-fotos. La tienda no lo mostrará hasta que subas otra.'
      )
    ) {
      return;
    }
    setBusy(true);
    setErr('');
    setWarn('');
    try {
      await api.clearProductoImagen(product.id);
      setOpciones([]);
      onUploaded?.({ ...product, imagen: null, opciones: [] });
    } catch (ex) {
      const msg = String(ex.message || '');
      if (/404|Cannot POST|Cannot DELETE|Falta la imagen/i.test(msg)) {
        setErr(
          'El API aún no tiene “quitar foto”. Sube el ZIP del API en api.ohbellezaoficial.com, reinicia Node y verifica /api/ping.'
        );
      } else {
        setErr(msg || 'No se pudo quitar la foto');
      }
    } finally {
      setBusy(false);
    }
  }

  const slots = opciones.length ? opciones : [];
  const canAdd = slots.filter((o) => o.id || o.imagen).length < MAX;
  const tieneAlgunaFoto = Boolean(product.imagen) || slots.some((o) => o.imagen);

  return (
    <div className="product-ops-editor">
      <p className="photo-opciones-hint">
        Sirve para cualquier producto (no solo exfoliantes). Hasta {MAX} sub-fotos: sabores, colores, presentaciones…
        Mismo código. Marca Agotada solo en esa sub-opción.
      </p>
      <div className="opcion-grid">
        {slots.map((op, idx) => {
          const img = imageUrl(op.imagen);
          return (
            <article key={op.id || `v-${idx}`} className={`opcion-card${op.disponible ? '' : ' is-agotada'}`}>
              <button
                type="button"
                className="opcion-card-img"
                disabled={busy}
                onClick={() => {
                  setUploadFor(op);
                  fileRef.current?.click();
                }}
                title="Cambiar foto"
              >
                {img ? <img src={img} alt="" /> : <span>Subir foto</span>}
                {!op.disponible ? <em className="opcion-agotada-badge">Agotada</em> : null}
              </button>
              <input
                className="opcion-nombre"
                placeholder={`Sub ${idx + 1} (sabor, color, talla…)`}
                defaultValue={op.nombre || ''}
                disabled={busy || !op.id}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v !== (op.nombre || '')) saveNombre(op, v);
                }}
              />
              <div className="opcion-card-actions">
                <button
                  type="button"
                  className={op.disponible ? 'btn-ghost' : 'btn-secondary'}
                  disabled={busy}
                  onClick={() => toggleDisp(op)}
                >
                  {op.disponible ? 'Disponible' : 'Agotada'}
                </button>
                {op.id ? (
                  <button type="button" className="btn-ghost danger" disabled={busy} onClick={() => removeOp(op)}>
                    Quitar
                  </button>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
      <div className="photo-modal-actions">
        <button type="button" className="btn-primary" disabled={busy || !canAdd} onClick={addSlot}>
          {busy ? 'Guardando…' : canAdd ? 'Agregar sub-foto / sabor' : `Máximo ${MAX}`}
        </button>
        {!slots.length ? (
          <button
            type="button"
            className="btn-secondary"
            disabled={busy}
            onClick={() => {
              setUploadFor({ id: null, virtual: true });
              fileRef.current?.click();
            }}
          >
            Subir primera foto
          </button>
        ) : null}
        {tieneAlgunaFoto ? (
          <button
            type="button"
            className="btn-secondary danger photo-modal-clear"
            disabled={busy}
            onClick={clearAllPhotos}
          >
            {busy ? 'Quitando…' : 'Dejar sin foto'}
          </button>
        ) : null}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        hidden
        onChange={onPickFile}
      />
      {warn ? <p className="photo-modal-warn">{warn}</p> : null}
      {err ? <p className="error">{err}</p> : null}
    </div>
  );
}

export default function ProductOpcionesModal({ product, onClose, onUploaded }) {
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!product) return null;

  return (
    <div className="photo-modal-backdrop" onClick={onClose} role="presentation">
      <div className="photo-modal photo-modal--opciones" onClick={(e) => e.stopPropagation()}>
        <header className="photo-modal-head">
          <div>
            <strong className="mono">{product.codigo}</strong>
            <span>{product.nombre}</span>
          </div>
          <button type="button" className="photo-modal-close" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </header>
        <ProductOpcionesEditor product={product} onUploaded={onUploaded} />
      </div>
    </div>
  );
}
